/**
 * Shared Square data cache.
 * 
 * Stores catalog, inventory, locations and devices in sessionStorage
 * so they load instantly when switching between inventory page and
 * invoice wizard. Background refresh keeps data fresh.
 */

const CACHE_KEY = 'square_shared_cache_v2';
const CACHE_TTL_MS = 10 * 60 * 1000; // 10 min

function _read() {
  try {
    const raw = window.sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    if (!parsed._ts || (Date.now() - parsed._ts) > CACHE_TTL_MS) {
      window.sessionStorage.removeItem(CACHE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function _write(data) {
  try {
    window.sessionStorage.setItem(CACHE_KEY, JSON.stringify({
      ...data,
      _ts: Date.now(),
    }));
  } catch {
    // best-effort
  }
}

/** Get cached Square data or null if expired/missing */
export function getSquareCache() {
  return _read();
}

/** Persist Square data to shared cache */
export function setSquareCache(data) {
  const existing = _read() || {};
  _write({ ...existing, ...data });
}

/** Invalidate the entire cache (after order creation, etc.) */
export function invalidateSquareCache() {
  window.sessionStorage.removeItem(CACHE_KEY);
}

/** Get specific fields if they exist and haven't expired */
export function getCachedCatalog() {
  const c = _read();
  return c?.catalog_items || null;
}

export function getCachedInventory() {
  const c = _read();
  return c?.inventory_counts || null;
}

export function getCachedLocations() {
  const c = _read();
  return c?.locations || null;
}

export function getCachedLocationId() {
  const c = _read();
  return c?.location_id || null;
}


// ── Custom category ordering (persisted in localStorage) ──
const CATEGORY_ORDER_KEY = 'square_category_order_v1';

/** Get saved category order: string[] or null */
export function getCategoryOrder() {
  try {
    const raw = window.localStorage.getItem(CATEGORY_ORDER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/** Save custom category order */
export function setCategoryOrder(order) {
  try {
    window.localStorage.setItem(CATEGORY_ORDER_KEY, JSON.stringify(order));
  } catch {
    // best-effort
  }
}

/**
 * Sort categories using the custom order.
 * Categories in the custom order come first (in that order),
 * then remaining categories alphabetically,
 * then 'Uncategorized' last.
 */
export function sortCategoriesWithCustomOrder(categories) {
  const customOrder = getCategoryOrder() || [];
  const orderMap = {};
  customOrder.forEach((cat, i) => { orderMap[cat] = i; });

  return [...categories].sort((a, b) => {
    if (a === 'Uncategorized') return 1;
    if (b === 'Uncategorized') return -1;
    const aIdx = orderMap[a];
    const bIdx = orderMap[b];
    const aHasOrder = aIdx !== undefined;
    const bHasOrder = bIdx !== undefined;
    if (aHasOrder && bHasOrder) return aIdx - bIdx;
    if (aHasOrder) return -1;
    if (bHasOrder) return 1;
    return a.localeCompare(b);
  });
}
